import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { AppRole } from "@monorepo/core";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const VIEW_ROLE_STORAGE_KEY = "mtg-deck-builder.view-role";

const getStringClaim = (value: unknown) =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const parseRoles = (value: unknown): AppRole[] => {
  if (!Array.isArray(value)) {
    return [AppRole.Member];
  }

  const roles = value.filter(
    (role): role is AppRole =>
      role === AppRole.Admin || role === AppRole.Member,
  );

  if (roles.includes(AppRole.Admin) && !roles.includes(AppRole.Member)) {
    roles.push(AppRole.Member);
  }

  return roles.length > 0 ? roles : [AppRole.Member];
};

export interface AuthContextValue {
  isAuthenticated: boolean;
  getIdToken: () => Promise<string | undefined>;
  logout: () => void;
  email?: string;
  roles: AppRole[];
  isAdmin: boolean;
  viewRole: AppRole;
  toggleViewRole: () => void;
  setViewRole: (role: AppRole) => void;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  getIdToken: () => Promise.resolve(""),
  logout: () => undefined,
  email: undefined,
  roles: [],
  isAdmin: false,
  viewRole: AppRole.Member,
  toggleViewRole: () => undefined,
  setViewRole: () => undefined,
});

interface ProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<ProviderProps> = ({ children }) => {
  const { user, signOut } = useAuthenticator((context: any) => [context.user]);
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [viewRole, setViewRole] = useState<AppRole>(AppRole.Member);
  const isAdmin = roles.includes(AppRole.Admin);

  useEffect(() => {
    let isActive = true;

    if (!user) {
      setEmail(undefined);
      setRoles([]);
      setViewRole(AppRole.Member);
      window.localStorage.removeItem(VIEW_ROLE_STORAGE_KEY);
      return () => {
        isActive = false;
      };
    }

    void fetchAuthSession()
      .then((session) => {
        if (!isActive) {
          return;
        }

        const payload = session.tokens?.idToken?.payload;
        const nextEmail =
          getStringClaim(payload?.email) ??
          getStringClaim(payload?.["cognito:username"]) ??
          getStringClaim(user?.signInDetails?.loginId);
        const nextRoles = parseRoles(payload?.["cognito:groups"]);
        const storedViewRole = window.localStorage.getItem(
          VIEW_ROLE_STORAGE_KEY,
        );
        const nextViewRole =
          nextRoles.includes(AppRole.Admin) && storedViewRole === AppRole.Member
            ? AppRole.Member
            : nextRoles.includes(AppRole.Admin)
              ? AppRole.Admin
              : AppRole.Member;

        setEmail(nextEmail);
        setRoles(nextRoles);
        setViewRole(nextViewRole);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        const fallbackEmail = getStringClaim(user?.signInDetails?.loginId);
        setEmail(fallbackEmail);
        setRoles([AppRole.Member]);
        setViewRole(AppRole.Member);
      });

    return () => {
      isActive = false;
    };
  }, [user]);

  useEffect(() => {
    if (!isAdmin) {
      window.localStorage.removeItem(VIEW_ROLE_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(VIEW_ROLE_STORAGE_KEY, viewRole);
  }, [isAdmin, viewRole]);

  const getIdToken = async () => {
    // fetchAuthSession automatically refreshes the accessToken and idToken if the tokens are expired and valid refreshToken presented.
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  };

  const logout = () => {
    signOut();
    navigate("/");
  };

  const toggleViewRole = useCallback(() => {
    if (!isAdmin) {
      return;
    }

    setViewRole((currentRole) =>
      currentRole === AppRole.Admin ? AppRole.Member : AppRole.Admin,
    );
  }, [isAdmin]);

  const setExplicitViewRole = useCallback(
    (role: AppRole) => {
      if (!isAdmin) {
        return;
      }

      setViewRole((currentRole) => (currentRole === role ? currentRole : role));
    },
    [isAdmin],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        getIdToken,
        logout,
        email,
        roles,
        isAdmin,
        viewRole,
        toggleViewRole,
        setViewRole: setExplicitViewRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
